import { connect } from 'react-redux';
import { createActionCable } from '../../../../utils/actioncable';
import React, { useEffect, useMemo, useRef } from 'react';
import { IconRss } from '../../../ui-kit/icons';
import SplitBadge from '../../../ui-kit/SplitBadge';
import PrioBadge from '../../../ui-kit/PrioBadge';
import moment from 'moment';
import SettingsForm from '../ui-kit/SettingsForm';
import { sortBy } from 'lodash';
import TagInput from '../../../ui-kit/TagInput';
import Loader from '../../../general/Loader';

import {
  operations as p2000MessagesOperations,
  selectors as p2000MessagesSelectors
} from '../../../../state/modules/p2000-messages';

const Settings = ({ updateWidgetSettingsField, widgetSettings }) => (
  <SettingsForm
    withAutoScroll={true}
    withAutoResizeHeight={true}
    updateWidgetSettingsField={updateWidgetSettingsField}
    widgetSettings={widgetSettings}
  >
    <SettingsForm.Section>
      <SettingsForm.Section.Header>
        {I18n.t('dashboards.widget_archetypes.p2000_messages.settings.addresses')}
      </SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        <TagInput
          value={widgetSettings.addresses || []}
          setValue={newValue => updateWidgetSettingsField('addresses', newValue)}
        />
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
  </SettingsForm>
);

const CapCodeDetails = ({ capCode }) => {
  if (!capCode) return null;
  if (!capCode.code) return null;

  const corpsAndRegion = [capCode.corps, capCode.region].filter(Boolean);
  const corpsAndRegionString = corpsAndRegion.length > 0 ? `(${corpsAndRegion.join(', ')})` : '';

  const details = `${capCode.description || ''} ${corpsAndRegionString}`.trim();

  return (
    <>
      <SplitBadge text1={capCode.code} text2={details} />
      <br />
    </>
  );
};

const P2000MessagesWidget = ({ messages, messagesLoaded, fetchMessages, registerForRealTimeUpdates, addresses }) => {
  useEffect(() => {
    fetchMessages({
      params: { addresses, created_after: moment().subtract(100, 'days').toISOString() },
      maxPages: 1,
      forceFetch: true,
      replace: true
    });
  }, [addresses, fetchMessages]);

  const subscriptionRef = useRef(null);

  useEffect(() => {
    subscriptionRef.current = registerForRealTimeUpdates({ addresses }, createActionCable());

    // Cleanup function to unsubscribe when the component unmounts or when
    // addresses change
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [addresses, registerForRealTimeUpdates]);

  const displayMessages = useMemo(() => {
    return sortBy(messages, ['timestamp']).reverse();
  }, [messages]);

  if (!messagesLoaded) return <Loader />;

  // create a table with columns body, cap_code.code, cap_code.corps,
  // cap_code.description, cap_code.region, timestamp
  return (
    <div className="list-group">
      {displayMessages.length === 0 && (
        <div className="text-center p-3">
          <IconRss size={'4x'} className="mb-2" />
          <p className="mb-0">{I18n.t('dashboards.widget_archetypes.p2000_messages.no_messages')}</p>
        </div>
      )}
      {displayMessages.map(message => (
        <div className="card mb-1" key={message.id}>
          <div className="card-header d-flex w-100 justify-content-between">
            <small className="text-nowrap">{moment(message.timestamp).format('HH:mm:ss | DD-MM-YYYY')}</small>
            <PrioBadge prio={message.prio} />
          </div>
          <div className="card-body py-1 overflow-hidden">
            <h5 className="card-title">{message.body}</h5>
            {message.cap_codes.map(capCode => (
              <CapCodeDetails key={capCode.code} capCode={capCode} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

Object.assign(P2000MessagesWidget, {
  SettingsContent: Settings,
  defaultSettings: {
    addresses: []
  }
});

const mapStateToProps = state => ({
  messages: p2000MessagesSelectors.values(state),
  messagesLoaded: p2000MessagesSelectors.initialLoadComplete(state)
});

const mapDispatchToProps = {
  fetchMessages: p2000MessagesOperations.fetch,
  registerForRealTimeUpdates: p2000MessagesOperations.registerForRealTimeUpdates
};

export default connect(mapStateToProps, mapDispatchToProps)(P2000MessagesWidget);
